import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Loader2, Play, Lock, ArrowRight, ClipboardList, Sparkles, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { api, extractError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { useAuth } from "../context/AuthContext";

function ProgressRing({ value, color, size = 72, stroke = 8 }) {
    const r = (size - stroke) / 2;
    const c = 2 * Math.PI * r;
    const pct = Math.max(0, Math.min(1, value));
    const dash = c * pct;
    return (
        <svg width={size} height={size} className="shrink-0">
            <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#E5EEF7" strokeWidth={stroke} />
            <circle
                cx={size / 2}
                cy={size / 2}
                r={r}
                fill="none"
                stroke={color}
                strokeWidth={stroke}
                strokeDasharray={`${dash} ${c - dash}`}
                strokeDashoffset={c * 0.25}
                strokeLinecap="round"
                transform={`rotate(-90 ${size / 2} ${size / 2})`}
                style={{ transition: "stroke-dasharray .4s ease" }}
            />
            <text
                x="50%"
                y="52%"
                textAnchor="middle"
                dominantBaseline="middle"
                fontFamily="Fraunces, serif"
                fontSize={size * 0.24}
                fontWeight="700"
                fill="#0B2545"
            >
                {Math.round(pct * 100)}%
            </text>
        </svg>
    );
}

const STATUS_BADGE = {
    draft: { label: "Draft", className: "bg-slate-100 text-slate-700 border-slate-200" },
    in_progress: { label: "In progress", className: "bg-amber-50 text-amber-800 border-amber-200" },
    locked: { label: "Locked", className: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

export default function CycleDetailPage() {
    const { cycleId } = useParams();
    const { user } = useAuth();
    const [cycle, setCycle] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    const navigate = useNavigate();

    const canManage = user?.role === "platform_admin" || user?.role === "org_leader";

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/cycles/${cycleId}`);
            setCycle(data);
            setError(null);
        } catch (e) {
            setError(extractError(e, "Could not load cycle"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cycleId]);

    const doStart = async () => {
        setBusy(true);
        try {
            await api.post(`/cycles/${cycleId}/start`);
            toast.success("Cycle started");
            load();
        } catch (e) {
            toast.error(extractError(e, "Could not start cycle"));
        } finally {
            setBusy(false);
        }
    };
    const doLock = async () => {
        if (!window.confirm("Lock this cycle? All data becomes read-only.")) return;
        setBusy(true);
        try {
            await api.post(`/cycles/${cycleId}/lock`);
            toast.success("Cycle locked");
            load();
        } catch (e) {
            toast.error(extractError(e, "Could not lock cycle"));
        } finally {
            setBusy(false);
        }
    };

    if (loading) {
        return (
            <div className="text-[#0B2545] flex items-center gap-3">
                <Loader2 className="animate-spin" size={18} /> Loading cycle…
            </div>
        );
    }
    if (error || !cycle) {
        return <div className="text-red-700">{error}</div>;
    }

    const badge = STATUS_BADGE[cycle.status] || STATUS_BADGE.draft;
    const isLocked = cycle.status === "locked";

    return (
        <div className="space-y-8" data-testid="cycle-detail-page">
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                    <Link to="/app/cycles" className="text-xs text-[#1E56A0] hover:underline">
                        ← All cycles
                    </Link>
                    <div className="mt-2 flex items-center gap-3 flex-wrap">
                        <h1
                            className="font-serif text-3xl sm:text-4xl font-semibold text-[#0B2545] leading-tight"
                            data-testid="cycle-name"
                        >
                            {cycle.name}
                        </h1>
                        <Badge className={badge.className + " border"} variant="outline">
                            {isLocked ? <Lock size={12} className="mr-1" /> : cycle.status === "in_progress" ? <Play size={12} className="mr-1" /> : null}
                            {badge.label}
                        </Badge>
                    </div>
                </div>

                {canManage && (
                    <div className="flex flex-wrap gap-2">
                        <Button
                            variant="outline"
                            onClick={() => navigate(`/app/cycles/${cycleId}/executive`)}
                            className="rounded-full border-[#0B2545] text-[#0B2545] hover:bg-[#0B2545] hover:text-white"
                            data-testid="open-executive-btn"
                        >
                            <TrendingUp size={14} /> Executive summary
                        </Button>
                        <Button
                            variant="outline"
                            onClick={() => navigate(`/app/cycles/${cycleId}/dashboard`)}
                            className="rounded-full border-[#0B2545] text-[#0B2545] hover:bg-[#0B2545] hover:text-white"
                            data-testid="open-dashboards-btn"
                        >
                            View dashboards
                        </Button>
                        {cycle.status === "draft" && (
                            <Button
                                onClick={doStart}
                                disabled={busy}
                                className="rounded-full bg-[#0B2545] hover:bg-[#1E56A0] text-white"
                                data-testid="start-cycle-btn"
                            >
                                <Play size={14} /> Start cycle
                            </Button>
                        )}
                        {!isLocked && (
                            <Button
                                variant="outline"
                                onClick={doLock}
                                disabled={busy}
                                className="rounded-full border-[#0B2545] text-[#0B2545] hover:bg-[#0B2545] hover:text-white"
                                data-testid="lock-cycle-btn"
                            >
                                <Lock size={14} /> Lock cycle
                            </Button>
                        )}
                    </div>
                )}
                {!canManage && (
                    <div className="flex flex-wrap gap-2">
                        {["reviewer", "coach"].includes(user?.role) && (
                            <Button
                                variant="outline"
                                onClick={() => navigate(`/app/cycles/${cycleId}/executive`)}
                                className="rounded-full border-[#0B2545] text-[#0B2545] hover:bg-[#0B2545] hover:text-white"
                                data-testid="open-executive-btn"
                            >
                                <TrendingUp size={14} /> Executive summary
                            </Button>
                        )}
                        <Button
                            variant="outline"
                            onClick={() => navigate(`/app/cycles/${cycleId}/dashboard`)}
                            className="rounded-full border-[#0B2545] text-[#0B2545] hover:bg-[#0B2545] hover:text-white"
                            data-testid="open-dashboards-btn"
                        >
                            View dashboards
                        </Button>
                    </div>
                )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="cycle-domain-grid">
                {cycle.domain_assessments.map((da) => {
                    const dom = da.domain || {};
                    const active = !!dom.is_active;
                    const pct = da.total_look_fors > 0 ? da.rated_count / da.total_look_fors : 0;
                    return (
                        <div
                            key={da.id}
                            className="rounded-2xl bg-white border border-[#D6E6F2] overflow-hidden hover:shadow-lg hover:shadow-[#0B2545]/5 transition-all"
                            data-testid={`domain-card-${dom.key}`}
                        >
                            <div
                                className="h-2 w-full"
                                style={{ backgroundColor: dom.color_hex || "#8AC4FF" }}
                            />
                            <div className="p-5 flex items-start justify-between gap-4">
                                <div className="min-w-0">
                                    <div className="text-[11px] uppercase tracking-[0.25em] font-bold" style={{ color: dom.color_hex }}>
                                        Domain
                                    </div>
                                    <div className="font-serif text-xl text-[#0B2545] font-semibold mt-1 leading-snug">
                                        {dom.name}
                                    </div>
                                    <div className="mt-3">
                                        {active ? (
                                            <>
                                                <div className="text-sm text-[#0E1B2C]/70">
                                                    {da.rated_count}/{da.total_look_fors} look-fors rated
                                                </div>
                                                {da.computed_domain_score != null && (
                                                    <div className="mt-1 text-sm">
                                                        Current score:{" "}
                                                        <span className="font-serif font-semibold text-[#0B2545]">
                                                            {da.computed_domain_score.toFixed(2)}
                                                        </span>
                                                        <span className="text-xs text-[#0E1B2C]/60"> / 4.0</span>
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <Badge className="bg-slate-100 text-slate-600 border-slate-200 border" variant="outline">
                                                <Sparkles size={10} className="mr-1" /> Coming soon
                                            </Badge>
                                        )}
                                    </div>
                                    {active && (
                                        <Button
                                            className="mt-4 rounded-full h-9 px-4 text-white text-sm"
                                            style={{ backgroundColor: dom.color_hex }}
                                            onClick={() => navigate(`/app/cycles/${cycleId}/domains/${dom.key}`)}
                                            data-testid={`domain-open-${dom.key}`}
                                        >
                                            {isLocked ? "View" : "Open scoring"}
                                            <ArrowRight size={14} />
                                        </Button>
                                    )}
                                </div>
                                {active && (
                                    <ProgressRing value={pct} color={dom.color_hex || "#1E56A0"} />
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
